import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Clock, Eye, LogOut, Mail, Loader2 } from 'lucide-react';

const HERO_BG = "https://customer-assets.emergentagent.com/job_gamma-to-web/artifacts/brgxn5xe_Grok%20Image%202026-02-05%20at%2017.41.51.png";

const PendingApprovalPage = () => {
    const { user, logout, checkAuth, isActive, loading } = useAuth();
    const navigate = useNavigate();

    // Redirect to dashboard if user becomes active
    useEffect(() => {
        if (isActive && !loading) {
            navigate('/dashboard', { replace: true });
        }
    }, [isActive, loading, navigate]);

    const handleLogout = async () => {
        await logout();
        navigate('/login', { replace: true });
    };

    const handleRefresh = async () => {
        await checkAuth();
        // The useEffect will handle redirect if status changed to active
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden">
            {/* Background image */}
            <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${HERO_BG})` }}
            />
            {/* Dark overlay for readability */}
            <div className="absolute inset-0 bg-background/70 backdrop-blur-[2px]" />

            {/* Logo and title */}
            <div className="text-center mb-8 z-10 fade-in">
                <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="p-3 rounded-lg bg-card border border-primary/30 shadow-gold">
                        <Eye className="w-8 h-8 text-primary" />
                    </div>
                </div>
                <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2">
                    The Decrypted English Method
                </h1>
            </div>

            {/* Pending Card */}
            <Card className="w-full max-w-md z-10 bg-card/80 backdrop-blur-sm border-border/50 shadow-elevated fade-in" style={{ animationDelay: '0.1s' }}>
                <CardHeader className="text-center pb-4">
                    <div className="mx-auto mb-4 p-4 rounded-full bg-amber-500/10 w-fit">
                        <Clock className="w-12 h-12 text-amber-500" />
                    </div>
                    <CardTitle className="text-xl">Account Pending Approval</CardTitle>
                    <CardDescription className="font-body text-base">
                        Welcome, {user?.name || 'Seeker'}! Your account is being reviewed.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
                        <div className="flex items-start gap-3">
                            <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
                            <div className="text-sm text-muted-foreground font-ui">
                                <p className="font-medium text-foreground mb-1">What happens next?</p>
                                <p>Once your payment is verified, your account will be activated and you'll have full access to all course content.</p>
                            </div>
                        </div>
                    </div>

                    <div className="text-center text-sm text-muted-foreground font-ui">
                        <p>Registered as: <span className="text-foreground">{user?.email}</span></p>
                    </div>

                    <div className="flex flex-col gap-3">
                        <Button
                            variant="outline"
                            size="lg"
                            className="w-full"
                            onClick={handleRefresh}
                            data-testid="check-status-btn"
                        >
                            Check Status
                        </Button>
                        <Button
                            variant="ghost"
                            size="lg"
                            className="w-full"
                            onClick={handleLogout}
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Footer text */}
            <p className="mt-8 text-center text-sm text-muted-foreground font-ui z-10">
                &copy; {new Date().getFullYear()} Gringos English. All rights reserved.
            </p>
        </div>
    );
};

export default PendingApprovalPage;
